/**
 * run-view.ts: SessionView, one run's state as a front-end renders it (V6 plan H22; the h2
 * designer's B.2), and the pure fold that computes it from the run's log and its run row.
 *
 * It is TeamRunView (run-team.ts) without the feed and without the console's own state (the lane
 * filter, the connection and the reducer index), plus the run-row fields the console polls
 * runs.get for: status, timestamps, cost, the outcome, the follow-up window, the budget and the
 * deliverables. The feed is run-feed.ts's fold, which reads this one's steps. run-team.ts builds
 * the console's TeamRunView on the two and re-exports both.
 *
 * Serializable: SessionViewSchema parses JSON.parse(JSON.stringify(view)) back to the same view.
 * Nothing in it is a Set, a Map, a function or a class instance, and nothing is undefined: an
 * absent value is null. The reducer's bookkeeping (TeamRunView's index) is either rebuilt from
 * the view's own fields (the threads the log created are the agents' threads; the open asks are
 * `asks`; the events seen are every sequence up to `throughSequence`) or kept as plain arrays in
 * `fold`, which renderers ignore.
 *
 * The fold is pure and deterministic: the same envelopes in the same order give a deep-equal
 * view, a view passed in is never changed, and an envelope at or below `throughSequence` (a
 * replay after a reconnect) changes nothing, but for one: the server fills a queued user event's
 * row in place (sink.ts), so its processed copy comes back at the sequence it already had, and
 * that copy takes the event out of `queued`. One run's log, ascending by sequence, as the run
 * stream sends it. Nothing here reads a clock.
 *
 * Reads: the wire's event, envelope, status, seat and usage types come from run-events.ts and
 * wire.ts. The rules (lead, state, done, ask open and close) are the ones run-team.ts has always
 * applied; their sources are listed there. The asks are also the server's ask ledger (V6 plan
 * H.47, decision D86): runs.command's tool_decision reads `asks` and `settledAsks`, so an ask
 * closes as the server's ledger closed it (a reply of the call's own kind, a result, its thread's
 * end, an interrupt or a verdict before it, a workflow run's thread aside).
 *
 * The setup lock: `setupLocked` (and `setupLockedByMode`, for a caller that has a mode but no view
 * yet) is the one predicate every configurator applies: the Blocks canvas, the configuration
 * panels, the code editor, the run preflight and the template upgrade banner are view-only while
 * it holds. It is a presentation rule over the view's mode, not a server lock: a run pins the
 * revision it started from, so a save cannot change a live run (V6 plan H.44, H416, D57).
 */
import { z } from "zod";
import { LimitationSchema } from "./capabilities.js";
import { OutcomeResultSchema, RUN_EVENT_TYPES, trackOrgRateLimit } from "./run-events.js";
import { isPauseStop, statusAtIdle, stopReasonOf, turnErrorAfter, } from "./run-status.js";
import { ENGINE_TARGETS, isRuntimeProvider, RuntimeProviderSchema, } from "./runtime.js";
import { toolGrantKey, toolGrantSubjectOf } from "./tool-grants.js";
import { RUN_STATUS_LIST, RunFinalizationStateSchema, RunSeatSchema, RunStatusSchema, RunUsageSchema, TERMINAL_RUN_STATUS_LIST, } from "./wire.js";
/** The closed enum: only these reach the wire (run-events.ts RUN_EVENT_TYPES). */
export { RUN_EVENT_TYPES };
/** runs.status's five terminal values: wire.ts TERMINAL_RUN_STATUS_LIST, the one list. */
export const TERMINAL_RUN_STATUSES = TERMINAL_RUN_STATUS_LIST;
/** The reserved advisor agent name on every engine (run-events.ts ThreadRef). Display it as "Advisor". */
export const ADVISOR_AGENT_NAME = "anthropic.advisor";
/** A write or edit under this path is a deliverable (DeliverableCard today). */
export const OUTPUTS_DIR = "/mnt/session/outputs/";
// ── the status rules every fold of a run's log shares ──
const KNOWN = new Set(RUN_EVENT_TYPES);
export function isKnownRunEvent(e) {
    return KNOWN.has(e.type);
}
/** The thread tag of any event (payload.session_thread_id), or undefined for the lead's rows. */
export function threadOf(e) {
    return str(e.payload?.session_thread_id);
}
/**
 * B5: the child thread a user.interrupt named, when it named one (`children`: the threads a
 * session.thread_created introduced). Null for an interrupt of the whole session: no thread id,
 * or the primary's (Claude may tag the primary's own rows with it). One that names a child
 * stopped that thread only: the turn goes on.
 */
export function interruptedThread(e, children) {
    if (e.type !== "user.interrupt")
        return null;
    const thread = str(e.payload?.session_thread_id);
    return thread && children.has(thread) ? thread : null;
}
export function isTerminalRunStatus(s) {
    return TERMINAL_RUN_STATUSES.includes(s);
}
/** The workflow runs a view has seen (by any of their rows) and not seen end (the Workflow runs page's count). */
export function openWorkflowRuns(view) {
    return view.workflows.filter((w) => w.state !== "ended").length;
}
/**
 * The run status a session idle sets while workflow runs are open (runs.ts run-status.ts
 * statusAtIdle): the work is not done. A pause stays a pause; after an interrupt the person says
 * whether the runs go on or stop (requires_action); any other idle reads running.
 */
export function runStatusWhileRunsOpen(status, interrupted) {
    if (status === "requires_action" || interrupted)
        return "requires_action";
    return "running";
}
export function newWorkflowDoneState() {
    return { open: new Set(), interrupted: false, runsInTurn: false, children: new Set() };
}
/** Fold one event of the run's own log in (`payload`: the event's body). */
export function trackWorkflowDone(state, type, payload) {
    const thread = payload.session_thread_id;
    if (type === "session.thread_created" && typeof thread === "string")
        state.children.add(thread);
    if (type === "user.interrupt" && !interruptedThread({ type, payload }, state.children))
        state.interrupted = true;
    if (type === "user.message" || type === "user.define_outcome") {
        state.interrupted = false;
        state.runsInTurn = state.open.size > 0;
    }
    const id = payload.workflow_run_id;
    if (typeof id !== "string" || id.length === 0)
        return;
    if (type === "workflow_run.created") {
        state.open.add(id);
        state.runsInTurn = true;
    }
    if (type === "workflow_run.status_ended")
        state.open.delete(id);
}
/**
 * The run status a session idle sets under the done rule (run-status.ts statusAtIdle): the
 * idle's own status (`settled`) stands only when no workflow run is open and no interrupt came
 * since the person's last message in a turn that had one. A run's end changes nothing: the lead
 * then gets a turn to read it, and that turn's idle decides. After an interrupt that turn might
 * not come, so the person continues (requires_action).
 */
export function runStatusUntilDone(settled, runs) {
    if (runs.open > 0 || (runs.interrupted && runs.runsInTurn))
        return runStatusWhileRunsOpen(settled, runs.interrupted);
    return settled;
}
/**
 * The state rule for an idle (thread or session): a pause on an ask → waiting; the budget → paused
 * (nothing to answer); an interrupt → stopped; retries exhausted → error; else idle.
 */
export function agentStateFromStop(reason) {
    if (reason === "requires_action")
        return "waiting";
    if (reason === "budget_reached")
        return "paused";
    if (reason === "user_interrupt" || reason === "interrupted")
        return "stopped";
    if (reason === "retries_exhausted")
        return "error";
    return "idle";
}
export const AgentRoleSchema = z.enum(["lead", "helper", "advisor", "grader"]);
/**
 * Copy: Working / Needs you / Idle / Finished / Stopped on an error / Stopped (an interrupt ended its
 * turn) / Paused at the budget (nothing to answer, so never counted as waiting). Always shown as text,
 * never colour alone.
 */
export const AgentStateSchema = z.enum([
    "not_started",
    "working",
    "waiting",
    "idle",
    "done",
    "error",
    "stopped",
    "paused",
]);
export const TokenTallySchema = z.object({
    input: z.number(),
    output: z.number(),
    cacheRead: z.number(),
    cacheCreation: z.number(),
});
/** One agent of the run, lead first: B.2's AgentView, plus the label and lane the console draws. */
export const AgentViewSchema = z.object({
    /** "lead", the thread id, or `seat:<name>` for a launch seat not yet spawned. */
    key: z.string(),
    /** null for the lead and for a seat not yet spawned. */
    thread: z.string().nullable(),
    /** planned: B5. The per-run alias ("t2") once the wire carries aliases. */
    alias: z.string().nullable(),
    /** The raw agent_name, e.g. "Research Brief" or "anthropic.advisor". */
    name: z.string(),
    /** What to print: "Advisor" for anthropic.advisor; same-named agents get " 2", " 3"… in order of arrival (stable). */
    label: z.string(),
    /** 0 = lead; helpers 1, 2… by arrival. Cycle the lane colour tokens with it; never rely on colour alone. */
    laneIndex: z.number().int().nonnegative(),
    role: AgentRoleSchema,
    state: AgentStateSchema,
    /** Canonical catalog id, e.g. "claude-sonnet-5" (seats). */
    model: z.string().nullable(),
    /** Catalog family word: claude, gpt, kimi, deepseek, glm, other (runs.team). */
    vendor: z.string().nullable(),
    provider: RuntimeProviderSchema.nullable(),
    /** The model or deployment that actually ran. */
    served: z.string().nullable(),
    /** Claude in the spec, another family served. */
    substituted: z.boolean().nullable(),
    /** Output tokens from this agent's span.model_request_end rows (what the card shows). */
    tokens: z.number(),
    usage: TokenTallySchema,
    /** List cost from usage.byAgent or the per-thread spend; null when not known yet. */
    costCents: z.number().nullable(),
    startedAt: z.string().nullable(),
    endedAt: z.string().nullable(),
    /**
     * B5: the thread's latest lifecycle row was session.thread_status_terminated (it finished or
     * was archived): it takes no Stop or Archive. null until a thread lifecycle row says either.
     */
    terminated: z.boolean().nullable(),
    /** The workflow run whose thread this is (its thread_created's workflow_run_id). */
    workflowRunId: z.string().nullable(),
    /** What the agent last did: "{agent} · {label}". */
    activity: z.object({ at: z.string(), label: z.string() }).nullable(),
    /** thread_message_received rows from this agent. */
    reports: z.number().int().nonnegative(),
});
/** How a workflow run ended. `other` is a result type this contract does not know: "ended some other way". */
export const WorkflowOutcomeSchema = z.enum(["completed", "stopped", "error", "other"]);
export const PhaseViewSchema = z.object({
    id: z.string(),
    /** The planned name; an unlisted phase is named by its id. */
    name: z.string(),
    /** `todo` is "not started". Only phase_started / phase_ended and the run's end move it. A phase lists no threads (S8). */
    state: z.enum(["todo", "running", "done", "failed"]),
    /** Started but not in workflow_run.created's list: shown by its id, never counted as "Phase n of N". */
    unlisted: z.boolean(),
});
/** One workflow run: TeamRunView's WorkflowBar, with null where the bar leaves a field out. */
export const WorkflowViewSchema = z.object({
    /** wrun_… today. Copy: "Workflow {id}: {name}", "Phase {n} of {total}: {name}". */
    id: z.string(),
    /** workflow_run.created's name; the run's id until a created event names it. */
    name: z.string(),
    description: z.string().nullable(),
    /** `idle` is a paused run; with no status row yet it is `planned`. Only the run's own status rows change it. */
    state: z.enum(["planned", "running", "idle", "ended"]),
    phases: z.array(PhaseViewSchema),
    /** The run's threads, in the order they joined (their thread_created named this run). */
    threads: z.array(z.string()),
    /** Drawn from a later row of the run, not from workflow_run.created: named by its id until one arrives. */
    planUnknown: z.boolean(),
    /** `completed` means the run finished, not that its work passed. */
    result: WorkflowOutcomeSchema.nullable(),
    error: z.string().nullable(),
    /** error.type: timeout_error, program_error, thread_limit_error, unknown_error, the loop's workflow_error… */
    errorType: z.string().nullable(),
    /** B6: the run's list cost, its threads summed (usage.byWorkflowRun); null until known. */
    costCents: z.number().nullable(),
});
/** One open ask, oldest first: B.2's AskView. Answer it with its `id`. */
export const AskViewSchema = z.object({
    /** The tool_use / custom_tool_use payload id. */
    id: z.string(),
    kind: z.enum(["permission", "question"]),
    /** null when the lead asks. */
    thread: z.string().nullable(),
    /** The asking agent's label: "Research Brief wants to use web_search". */
    agentName: z.string(),
    at: z.string(),
    tool: z.string().nullable(),
    mcpServer: z.string().nullable(),
    /** The salient argument: command, path, url, query. */
    preview: z.string().nullable(),
    input: z.record(z.string(), z.unknown()).nullable(),
    question: z.string().nullable(),
    context: z.string().nullable(),
    responseMode: z.enum(["text", "choice", "confirmation"]).nullable(),
    choices: z.array(z.string()).nullable(),
    quickReplies: z.array(z.string()).nullable(),
    /** False when the run's policy forces every ask to a person (hitlForced), and for questions. */
    canAlwaysAllow: z.boolean(),
    /**
     * The server's own sweep will allow it: `grant` when an Always allow this run holds covers it
     * (toolGrants), `toolbelt` when the run's tool settings do. A renderer shows it as automatic.
     */
    coveredBy: z.enum(["grant", "toolbelt"]).nullable(),
    /** The Always-allow key of its tool (tool-grants.ts toolGrantKey); null for a question. */
    grantKey: z.string().nullable(),
    /**
     * A requires_action idle named it: the engine is waiting on it. A loop engine logs an ask
     * before it waits on it and drops an answer that comes before, so the server holds a decision
     * on an ask not named yet until it is (wire 5.13.0). Optional on the wire: a server before it
     * sends none, read as false.
     */
    named: z.boolean().optional(),
});
/**
 * How an ask that left `asks` was settled, as the server's 409s read it: `answered` when a
 * reply of the call's own kind (a confirmation for a tool call, a custom tool result for a
 * question) or the call's result came, `closed` when its thread terminated before anything
 * answered it (a reply after that has no effect).
 */
export const SettledAskSchema = z.enum(["answered", "closed"]);
/** How many settled asks a view keeps, the last settled first to stay. */
export const SETTLED_ASKS_MAX = 200;
/** The team's figures for the header chips. */
export const TeamTotalsSchema = z.object({
    /** Output tokens across every agent: the team's figure for the header chip. */
    tokens: z.number(),
    graderTokens: z.number(),
    /** Whole-run cost from the run row's usage; null until known. Per-agent figures do not sum to it. */
    costCents: z.number().nullable(),
    costState: z.enum(["estimated", "settled", "provider_reported", "unavailable", "unknown"]),
    helpers: z.number().int().nonnegative(),
    working: z.number().int().nonnegative(),
    waiting: z.number().int().nonnegative(),
    searches: z.number().int().nonnegative(),
});
/** What the console shows the run doing: today's ConsoleMode, minus no-session. */
export const SessionModeSchema = z.enum([
    "preparing",
    "working",
    "needs-you",
    "finishing",
    "completed-resumable",
    "completed",
    "terminated",
]);
/**
 * The setup lock (owner decision 2026-10-15; V6 plan H.44): the configuration is view-only from
 * the click on Run until the run is no longer live. `starting` is the start request in flight
 * (the save, the tool sync and the start call), before the run has a view; then the lock holds
 * while the mode is preparing, working or needs-you. "finishing" and the ended modes do not lock,
 * and no run (a null mode) with nothing starting is unlocked.
 */
export function setupLockedByMode(mode, starting) {
    if (starting)
        return true;
    return mode === "preparing" || mode === "working" || mode === "needs-you";
}
/** The setup lock over a run's view: see setupLockedByMode. A null view is no run. */
export function setupLocked(input) {
    return setupLockedByMode(input.view?.mode ?? null, input.starting);
}
/** What the run waits on the person for, when it does. */
export const SessionNeedsSchema = z.enum([
    "permission",
    "question",
    "budget",
    "rate_limit",
    "workflow_runs",
]);
/** Whether a finished run still takes a follow-up message, and if not, why not. */
export const SessionFollowUpSchema = z.object({
    open: z.boolean(),
    closedReason: z.enum(["failed", "expired", "superseded"]).nullable(),
    /** When the window closes, when the server says. */
    until: z.string().nullable(),
});
export const SessionCapabilitiesSchema = z.object({
    canSend: z.boolean(),
    canInterrupt: z.boolean(),
    canStartNew: z.boolean(),
    /** A message to the finished run is its next turn (the run row's canResume). */
    canFollowUp: z.boolean(),
    /** The run is live and its cap is under the maximum. */
    canRaiseBudget: z.boolean(),
    /** False when the run's policy forces every ask to a person (hitlForced). */
    canAlwaysAllow: z.boolean(),
});
export const SessionBudgetSchema = z.object({
    capCents: z.number(),
    maxCents: z.number(),
});
export const SessionOutputsSchema = z.object({
    count: z.number().int().nonnegative(),
    finalization: RunFinalizationStateSchema,
});
/**
 * The run's parent session as the run row has it: `unknown` until a row that carries it
 * arrives, `none` when the run has no session (it never had one, or predates the model),
 * `terminated` when the provider ended it, else `open`. `resumeReady`: the output snapshot is
 * ready, so a follow-up turn may start.
 */
export const SessionLifecycleSchema = z.object({
    state: z.enum(["unknown", "none", "open", "terminated"]),
    resumeReady: z.boolean(),
});
/** A launch seat's routing, by agent name (the run row's seats, or runs.team). */
export const SeatInfoSchema = z.object({
    name: z.string(),
    role: AgentRoleSchema,
    model: z.string().nullable(),
    vendor: z.string().nullable(),
    provider: RuntimeProviderSchema.nullable(),
    served: z.string().nullable(),
    substituted: z.boolean().nullable(),
});
/** An ask an interrupt or a verdict cut from `asks`, as the fold keeps it until its row is decided. */
const CutAskSchema = z.object({
    id: z.string(),
    kind: z.enum(["permission", "question"]),
    thread: z.string().nullable(),
});
/**
 * The fold's own bookkeeping, plain JSON: what it needs to read the next envelope that the view's
 * other fields do not already say. Renderers ignore it. Its lists are bounded.
 */
export const SessionFoldSchema = z.object({
    /**
     * The fold's shape (SESSION_FOLD_VERSION). A view without one is from before it (an older
     * release's frame, a kept or stored snapshot): normaliseSessionView fills what it lacks before
     * the fold goes on. Optional on the wire: a server before it sends none.
     */
    foldVersion: z.number().int().nonnegative().optional(),
    /** The runId of the last envelope folded in (an earlier turn's rows carry another). */
    lastRunId: z.string().nullable(),
    /**
     * The log settled the run since its turn began: the last status a row of the log set (the
     * session's running row aside) is a terminal one, so the person's next user.message opens the
     * next turn (`turn`). Optional on the wire: a server before it sends none, read as false.
     */
    turnEnded: z.boolean().optional(),
    /** The latest session lifecycle row applied: an older one never regresses the status. */
    lifecycle: z.object({ at: z.string(), seq: z.number() }).nullable(),
    /** The stop reason of the lead's last session.status_idle, while no status_running came after it. */
    idleReason: z.string().nullable(),
    /** A user.interrupt reached the lead since the person's last user.message or user.define_outcome. */
    leadInterrupted: z.boolean(),
    /** A workflow run was open at some point since the person's last message (the done rule). */
    runsInTurn: z.boolean(),
    /**
     * The workflow runs this run's own rows created and not seen end: the done rule's count
     * (run-status.ts statusAtIdle), as the server counts its run's log. `workflows` draws every
     * run the log names, an earlier turn's included.
     */
    openRuns: z.array(z.string()),
    /**
     * The grader's verdict on this turn (span.outcome_evaluation_end), which the person's next
     * message clears: the done rule reads it. `outcome` shows the latest verdict.
     */
    verdict: z.string().nullable(),
    /**
     * A lead's session.error that ended the turn, by its type (run-status.ts turnErrorAfter),
     * until the lead works again or the person's next message.
     */
    turnError: z.string().nullable(),
    /** Helper threads a user.interrupt reached; one that next idles without doing more work was stopped. */
    interrupted: z.array(z.string()),
    /** Inside an outcome evaluation: tool rows from a thread the log never created are the grader's. */
    grading: z.boolean(),
    /** Every open phase: the ids its start goes by (the event's and its payload's), its run and phase. */
    openPhases: z.array(z.object({ ids: z.array(z.string()), workflowId: z.string(), phaseId: z.string() })),
    /** The launch seats' routing, for a helper card made later. */
    seats: z.array(SeatInfoSchema),
    /** Hashes of the delegations drawn (`to|body`), so Claude's helper-side copy is not drawn twice. */
    delegated: z.array(z.string()),
    /** Hashes of the model requests counted (model_request_start_id), so a copy is not counted twice. */
    tokenKeys: z.array(z.string()),
    /** Threads the organization's per-minute limit stopped since the person's last message ("" the primary). */
    rateLimited: z.array(z.object({ thread: z.string(), at: z.string() })),
    /**
     * Asks an interrupt or a grader's verdict cut (wire 5.13.0): they left `asks`, since nothing
     * can answer them now (the server's ask ledger reads them so), and the next word on each call
     * says how its row reads: a result or a reply as it says, its thread's end closed, the turn's
     * end cancelled. The last SETTLED_ASKS_MAX. Optional on the wire: a server before it sends none.
     */
    cutAsks: z.array(CutAskSchema).optional(),
});
/**
 * The fold's shape this release writes. 1: the turn's status facts (`openRuns`, `verdict`,
 * `turnError`, #414), the turn's end (`turnEnded`, #408) and the cut asks (`cutAsks`, #421) are
 * all present. A change to the fold's fields or their meaning takes the next number, and
 * normaliseSessionView learns to bring the one before it up.
 */
export const SESSION_FOLD_VERSION = 1;
export const SESSION_VIEW_VERSION = 1;
/** One run's state as a front-end renders it (B.2). Serializable: parse(JSON) of a view is the view. */
export const SessionViewSchema = z.object({
    version: z.literal(SESSION_VIEW_VERSION),
    runId: z.string(),
    projectId: z.string().nullable(),
    /** The last log sequence folded in (-1 before any). */
    throughSequence: z.number().int().min(-1),
    /**
     * The turn the run is on, counted from the log: 1, then one more at each follow-up turn, a
     * user.message after the log settled the run (the server takes a message to a finished run
     * as its next turn) or the rows of another run (an earlier turn's rows carry another runId).
     * Optional on the wire: a server before it sends none, read as 1.
     */
    turn: z.number().int().positive().optional(),
    /** Labels; no renderer branches on them. */
    provider: RuntimeProviderSchema.nullable(),
    runner: z.enum(ENGINE_TARGETS).nullable(),
    /** The run's title: the mission's first line. */
    title: z.string(),
    /** The lead's name (runs.get agentName); "" until known. */
    leadName: z.string(),
    model: z.string().nullable(),
    /** RunStatus, from the one done rule. */
    status: RunStatusSchema,
    stopReason: z.string().nullable(),
    /** Stop was sent; show "Stopping…" until the next session lifecycle row. */
    stopping: z.boolean(),
    /** The latest grader verdict, when the run has one. */
    outcome: OutcomeResultSchema.nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    mode: SessionModeSchema,
    needs: SessionNeedsSchema.nullable(),
    followUp: SessionFollowUpSchema,
    capabilities: SessionCapabilitiesSchema,
    session: SessionLifecycleSchema,
    /** agents[0] is always the lead. */
    agents: z.array(AgentViewSchema),
    /** Phases list no threads (S8). */
    workflows: z.array(WorkflowViewSchema),
    /** Open asks, oldest first. */
    asks: z.array(AskViewSchema),
    /**
     * The asks that left `asks` answered or closed, by ask id, in the order they settled: the last
     * SETTLED_ASKS_MAX (wire 5.13.0). An ask an interrupt or a verdict cut (fold.cutAsks) is in
     * neither until a later result, reply or the end of its thread settles it; one the turn's end
     * cancelled stays in neither. A result after its thread ended still makes a closed ask answered. runs.command's tool_decision reads both lists for its
     * answers (409 command_target_already_answered, command_target_closed). Optional on the wire: a
     * server before it sends none.
     */
    settledAsks: z.record(z.string(), SettledAskSchema).optional(),
    /** User event ids still stored with processed_at null. */
    queued: z.array(z.string()),
    /** The session's cap and the server's maximum; null until a run row carries them. */
    budget: SessionBudgetSchema.nullable(),
    /** As runs.get computes it; null until a run row carries it. */
    usage: RunUsageSchema.nullable(),
    totals: TeamTotalsSchema,
    outputs: SessionOutputsSchema,
    seats: z.array(RunSeatSchema).nullable(),
    limitations: z.array(LimitationSchema),
    /** The tools this run no longer asks for (Always allow), as toolGrantKey keys. */
    toolGrants: z.array(z.string()),
    fold: SessionFoldSchema,
});
/**
 * The run stream's view frame (`event: view`, no `id:`), written only to a stream opened with
 * `?view=1` (V6 plan H23; the h2 designer's B.1): the server's SessionView of the run, and the
 * last log sequence folded into it, which is never past the last envelope that connection has
 * written. At most one every 250 ms, and only when the view changed since the connection's last.
 */
export const RunStreamViewSchema = z.object({
    throughSequence: z.number().int().min(-1),
    view: SessionViewSchema,
});
// ═══════════════════════════════════════════════════════════════════════════════════════════
// §3  The fold
// ═══════════════════════════════════════════════════════════════════════════════════════════
/** A view of a run nothing has been folded into yet: its run row, an empty log. */
export function emptySessionView(run) {
    const [prefix] = (run.profileKey ?? "").split("/");
    const provider = run.provider ?? (isRuntimeProvider(prefix) ? prefix : null);
    const leadName = run.agentName ?? "";
    const name = leadName || "Agent";
    const lead = {
        key: "lead",
        thread: null,
        alias: null,
        name,
        label: name,
        laneIndex: 0,
        role: "lead",
        state: leadStateFromStatus(run.status, run.stopReason ?? undefined),
        model: run.model ?? null,
        vendor: null,
        provider,
        served: null,
        substituted: null,
        tokens: 0,
        usage: zeroUsage(),
        costCents: null,
        startedAt: run.createdAt ?? null,
        endedAt: null,
        terminated: null,
        workflowRunId: null,
        activity: null,
        reports: 0,
    };
    const base = {
        version: SESSION_VIEW_VERSION,
        runId: run.id ?? "",
        projectId: run.projectId ?? null,
        throughSequence: -1,
        turn: 1,
        provider,
        runner: run.engineTarget ?? null,
        title: run.title ?? firstLine(run.mission),
        leadName,
        model: run.model ?? null,
        status: run.status,
        stopReason: run.stopReason ?? null,
        stopping: false,
        outcome: null,
        createdAt: run.createdAt ?? null,
        updatedAt: run.updatedAt ?? null,
        mode: "preparing",
        needs: null,
        followUp: { open: false, closedReason: null, until: null },
        capabilities: {
            canSend: false,
            canInterrupt: false,
            canStartNew: false,
            canFollowUp: false,
            canRaiseBudget: false,
            canAlwaysAllow: !run.hitlForced,
        },
        session: { state: "unknown", resumeReady: false },
        agents: [lead],
        workflows: [],
        asks: [],
        settledAsks: {},
        queued: [],
        budget: null,
        usage: null,
        totals: {
            tokens: 0,
            graderTokens: 0,
            costCents: null,
            costState: "unknown",
            helpers: 0,
            working: 0,
            waiting: 0,
            searches: 0,
        },
        outputs: { count: 0, finalization: "not_started" },
        seats: null,
        limitations: [],
        toolGrants: [],
        fold: emptyFold(),
    };
    return foldRunRow(base, run);
}
/** The bookkeeping of a fold nothing has been folded into yet. */
function emptyFold() {
    return {
        foldVersion: SESSION_FOLD_VERSION,
        lastRunId: null,
        turnEnded: false,
        lifecycle: null,
        idleReason: null,
        leadInterrupted: false,
        runsInTurn: false,
        openRuns: [],
        verdict: null,
        turnError: null,
        interrupted: [],
        grading: false,
        openPhases: [],
        seats: [],
        delegated: [],
        tokenKeys: [],
        rateLimited: [],
        cutAsks: [],
    };
}
/**
 * A view as this release folds it. A view from before SESSION_FOLD_VERSION (a frame an older
 * release served, a view a client kept, a stored snapshot) lacks the fields later releases
 * added, and folding on it threw at the first one it read: the envelope was skipped and the
 * sequence moved on, so an end_turn could leave the run working for good. Each missing fold
 * field is filled. The turn's status facts take what the fold before them read: the workflow
 * runs drawn and not ended (it counted every one), the latest verdict, and whether the turn
 * ended (a terminal status). The rest take their empty values. A view at this version or later
 * comes back as it is, and so does a value that is not a view at all (the schema refuses it).
 * Never changes the view it is given.
 */
export function normaliseSessionView(view) {
    if (!isRecord(view))
        return view;
    const fold = isRecord(view.fold) ? view.fold : {};
    const at = fold.foldVersion;
    if (typeof at === "number" && at >= SESSION_FOLD_VERSION)
        return view;
    const workflows = Array.isArray(view.workflows) ? view.workflows : [];
    const before = {
        openRuns: workflows.flatMap((w) => isRecord(w) && w.state !== "ended" && typeof w.id === "string" ? [w.id] : []),
        verdict: typeof view.outcome === "string" ? view.outcome : null,
        turnEnded: isTerminalRunStatus(view.status),
    };
    const filled = { ...fold };
    for (const [key, value] of Object.entries({ ...emptyFold(), ...before }))
        if (filled[key] === undefined)
            filled[key] = value;
    filled.foldVersion = SESSION_FOLD_VERSION;
    return {
        ...view,
        settledAsks: view.settledAsks ?? {},
        fold: filled,
    };
}
/**
 * The fold: one envelope of the run's log in, the next view out. It dedupes by sequence and
 * processed state. An envelope at or below `throughSequence` (a duplicate, or a replay after a
 * reconnect) returns `view` itself, except a queued user event's processed copy: the server
 * fills that row in place, keeping its sequence and streaming nothing (sink.ts), so a replay, a
 * page of runs.events or a resumed stream brings it back at the sequence the view already
 * folded. That copy takes the event out of `queued` and changes nothing else.
 */
export function foldSessionView(given, envelope) {
    const view = normaliseSessionView(given);
    if (!readable(envelope) || typeof envelope.sequence !== "number")
        return view;
    if (envelope.sequence <= view.throughSequence)
        return filledInPlace(view, envelope);
    return stepSessionView(view, envelope).view;
}
/**
 * One envelope in, with what it did, for a feed fold to follow (run-feed.ts foldFeed). It does not
 * dedupe (foldSessionView does, by sequence and processed state; the console's reducer does, by
 * event id) and does not move `throughSequence` back.
 */
export function stepSessionView(given, envelope) {
    const view = normaliseSessionView(given);
    if (!readable(envelope))
        return { envelope, view, effects: noEffects(), skipped: true };
    let step = tryStep(view, envelope);
    let foldReset = false;
    if (!step && !SessionFoldSchema.safeParse(view.fold).success) {
        // The fold itself is what could not be read (normalising could not mend it): fold the
        // envelope on an empty one rather than skip it, so an idle still ends the turn.
        step = tryStep({ ...view, fold: emptyFold() }, envelope);
        foldReset = step !== null;
    }
    // A malformed payload must not take a renderer down: the envelope is skipped whole.
    const next = step ? step.view : { ...view };
    if (typeof envelope.sequence === "number" && envelope.sequence > next.throughSequence)
        next.throughSequence = Math.floor(envelope.sequence);
    return {
        envelope,
        view: next,
        effects: step ? step.effects : noEffects(),
        skipped: !step,
        ...(foldReset ? { foldReset: true } : {}),
    };
}
/** One envelope folded into `view`, or null when folding it threw. */
function tryStep(view, envelope) {
    try {
        const effects = noEffects();
        const next = draft(view);
        apply(next, envelope, effects);
        countTurn(view, next, envelope, effects);
        return { view: settle(next), effects };
    }
    catch {
        return null;
    }
}
/**
 * Fold a run row in (runs.get's detail, or any part of it): the authoritative status, button
 * gating, run cost, launch seats, per-agent and per-workflow-run spend, the session, the
 * deliverables, the follow-up window and the budget. A field the row leaves out keeps its value.
 */
export function foldRunRow(view, row) {
    const d = draft(normaliseSessionView(view));
    const [prefix, canonical] = (row.profileKey ?? "").split("/");
    const provider = row.provider ?? (isRuntimeProvider(prefix) ? prefix : d.provider);
    if (row.id !== undefined && !d.runId)
        d.runId = row.id;
    if (row.projectId !== undefined)
        d.projectId = row.projectId;
    if (row.engineTarget !== undefined)
        d.runner = row.engineTarget;
    if (row.title !== undefined)
        d.title = row.title;
    else if (row.mission !== undefined)
        d.title = firstLine(row.mission);
    if (row.createdAt !== undefined)
        d.createdAt = row.createdAt;
    if (row.updatedAt !== undefined)
        d.updatedAt = row.updatedAt;
    d.status = row.status;
    if (row.stopReason !== undefined)
        d.stopReason = row.stopReason;
    if (row.agentName !== undefined)
        d.leadName = d.leadName || row.agentName;
    d.model = row.model ?? canonical ?? d.model;
    d.provider = provider;
    const caps = d.capabilities;
    if (row.canSend !== undefined)
        caps.canSend = row.canSend;
    if (row.canInterrupt !== undefined)
        caps.canInterrupt = row.canInterrupt;
    if (row.canResume !== undefined)
        caps.canFollowUp = row.canResume;
    if (row.canStartNew !== undefined)
        caps.canStartNew = row.canStartNew;
    if (row.hitlForced !== undefined)
        caps.canAlwaysAllow = !row.hitlForced;
    if (isTerminalRunStatus(row.status))
        d.stopping = false;
    if (row.usage)
        d.usage = row.usage;
    if (row.finalizationStatus !== undefined)
        d.outputs.finalization = row.finalizationStatus;
    if (row.outputCount !== undefined)
        d.outputs.count = row.outputCount;
    if (row.session !== undefined)
        d.session =
            row.session === null
                ? { state: "none", resumeReady: false }
                : {
                    state: row.session.providerStatus === "terminated" ? "terminated" : "open",
                    resumeReady: row.session.finalization.resumeReady,
                };
    if (row.limitations !== undefined)
        d.limitations = row.limitations;
    if (row.toolGrants !== undefined)
        d.toolGrants = row.toolGrants;
    if (row.budget !== undefined)
        d.budget = row.budget;
    d.followUp = followUpOf(d.followUp, row);
    patchAgent(d, "lead", (a) => ({
        model: a.model ?? canonical ?? row.model ?? null,
        provider: a.provider ?? provider,
    }));
    if (!view.leadName && row.agentName)
        renameLead(d, row.agentName);
    if (row.seats !== undefined)
        d.seats = row.seats;
    for (const s of row.seats ?? []) {
        const role = s.role === "root" ? "lead" : s.role === "advisor" ? "advisor" : "helper";
        const info = {
            name: s.name,
            role,
            model: s.canonical,
            vendor: null,
            provider: s.provider,
            served: s.served,
            substituted: s.substituted,
        };
        setSeat(d, info);
        const matches = d.agents.filter((a) => role === "lead"
            ? a.key === "lead"
            : a.role === role && (a.name === s.name || role === "advisor"));
        for (const a of matches)
            patchAgent(d, a.key, {
                model: a.model ?? info.model,
                provider: a.provider ?? info.provider,
                served: a.served ?? info.served,
                substituted: a.substituted ?? info.substituted,
            });
        if (!matches.length && role !== "lead")
            d.agents.push({
                ...newAgent(d, `seat:${s.name}`, null, s.name, role),
                model: info.model,
                provider: info.provider,
                served: info.served,
                substituted: info.substituted,
                role,
            });
    }
    for (const g of row.usage?.byAgent ?? []) {
        if (g.costCents === null)
            continue;
        // A group's cost lands on a card only when the group is that one card: an agent that ran
        // on several threads has one figure for all of them, which no one card can claim.
        const cards = g.kind === "primary"
            ? d.agents.slice(0, 1)
            : g.kind === "advisor"
                ? d.agents.filter((a) => a.role === "advisor")
                : d.agents.filter((a) => a.thread !== null && a.name === g.agentName);
        if (cards.length === 1 && cards[0])
            patchAgent(d, cards[0].key, { costCents: g.costCents });
    }
    for (const w of row.usage?.byWorkflowRun ?? []) {
        const i = d.workflows.findIndex((x) => x.id === w.workflowRunId);
        const bar = d.workflows[i];
        if (!bar || w.costCents === null || bar.costCents === w.costCents)
            continue;
        d.workflows[i] = { ...bar, costCents: w.costCents };
    }
    return settle(d);
}
/** planned: B13. Fold a runs.team poll in: model, vendor, provider, cost, unspawned seats, run threads. */
export function foldTeamRoster(view, roster) {
    const d = draft(view);
    for (const r of roster.agents) {
        const role = r.role;
        setSeat(d, {
            name: r.name,
            role,
            model: r.model,
            vendor: r.vendor,
            provider: r.provider,
            served: r.served ?? null,
            substituted: r.substituted ?? null,
        });
        let key = r.role === "lead"
            ? "lead"
            : r.thread !== null
                ? d.agents.find((a) => a.key === r.thread || a.alias === r.thread)?.key
                : d.agents.find((a) => a.thread === null && a.role !== "lead" && a.name === r.name)?.key;
        if (key === undefined) {
            if (r.thread !== null) {
                ensureHelper(d, r.thread, r.name, r.startedAt ?? undefined);
                key = r.thread;
            }
            else {
                key = `seat:${r.name}`;
                d.agents.push(newAgent(d, key, null, r.name, role));
            }
        }
        const fromEvents = d.agents.find((a) => a.key === key)?.state;
        patchAgent(d, key, (a) => ({
            model: r.model,
            vendor: r.vendor,
            provider: r.provider,
            served: r.served ?? a.served,
            substituted: r.substituted ?? a.substituted,
            state: a.key === "lead" ||
                (fromEvents !== undefined && fromEvents !== "not_started" && a.thread !== null)
                ? a.state
                : r.state,
            startedAt: a.startedAt ?? r.startedAt ?? null,
            endedAt: a.endedAt ?? r.endedAt ?? null,
            ...(r.costCents !== null ? { costCents: r.costCents } : {}),
            ...(r.usage && r.usage.output_tokens > a.tokens
                ? { tokens: r.usage.output_tokens, usage: addUsage(zeroUsage(), r.usage) }
                : {}),
        }));
    }
    for (const w of roster.workflows) {
        const i = d.workflows.findIndex((x) => x.id === w.id);
        const known = d.workflows[i];
        if (!known) {
            d.workflows.push({
                id: w.id,
                name: w.name,
                description: null,
                state: w.result ? "ended" : "running",
                phases: w.phases.map((p) => ({ id: p.id, name: p.name, state: p.state, unlisted: false })),
                threads: [...w.threads],
                planUnknown: false,
                result: w.result ?? null,
                error: null,
                errorType: null,
                costCents: null,
            });
            continue;
        }
        d.workflows[i] = {
            ...known,
            result: known.result ?? w.result ?? null,
            threads: [...new Set([...known.threads, ...w.threads])],
        };
    }
    return settle(d);
}
/** Fold a client's own header facts in. A key given as undefined clears the field, as a header merge does. */
export function foldHeader(view, patch) {
    const d = draft(view);
    if ("title" in patch)
        d.title = patch.title ?? "";
    if ("model" in patch)
        d.model = patch.model ?? null;
    if ("provider" in patch)
        d.provider = patch.provider ?? null;
    if ("hitlForced" in patch)
        d.capabilities.canAlwaysAllow = !patch.hitlForced;
    if ("stopping" in patch)
        d.stopping = patch.stopping === true;
    if ("leadName" in patch) {
        d.leadName = patch.leadName ?? "";
        if (patch.leadName && patch.leadName !== view.leadName)
            renameLead(d, patch.leadName);
    }
    return settle(d);
}
/** The lane a thread's rows sit in: the lead rule, a thread the log never created is the lead (conformance.ts:42-64). */
export function laneOf(view, thread) {
    return thread && created(view, thread) ? thread : "lead";
}
/** An agent by lane key; the lead when there is none. */
export function agentOf(view, key) {
    return view.agents.find((a) => a.key === key) ?? view.agents[0];
}
/** During an outcome evaluation the grader works in a thread the log never created: its tool rows are not the lead's. */
export function isGraderThread(view, thread) {
    return Boolean(view.fold.grading && thread && !created(view, thread));
}
/** The lane an ask sits in. */
export function laneOfAsk(ask) {
    return ask.thread ?? "lead";
}
/** Show the team panel: more than one agent, or any workflow. A single-agent run renders as today. */
export function isTeamView(view) {
    return view.agents.length > 1 || view.workflows.length > 0;
}
// ── shared readers (run-feed.ts draws its rows with the same ones) ──
/** The text the platform puts in the result of a call an interrupt cut short. */
export const INTERRUPTED_CALL = /tool execution was interrupted before completion/i;
export function str(v) {
    return typeof v === "string" && v ? v : undefined;
}
export function isRecord(v) {
    return typeof v === "object" && v !== null && !Array.isArray(v);
}
export function textOf(blocks) {
    return (blocks ?? [])
        .map((b) => (b.type === "text" && typeof b.text === "string" ? b.text : ""))
        .filter(Boolean)
        .join("\n\n");
}
export function contentText(blocks) {
    return (blocks ?? [])
        .map((b) => {
        if (b.type === "text")
            return b.text;
        if (b.type === "image")
            return "[image]";
        if (b.type === "document")
            return b.title || "[document]";
        return `${b.title ?? b.source ?? "Result"}\n${textOf(b.content)}`;
    })
        .join("\n\n");
}
/** The argument worth showing: command, query, path, url, pattern. */
export function salientOf(input) {
    for (const k of ["command", "query", "file_path", "path", "url", "pattern"]) {
        const v = str(input[k]);
        if (v)
            return v.length > 160 ? `${v.slice(0, 157)}…` : v;
    }
    return undefined;
}
/** What a tool call reads as on its agent's card: "Searching: …", "Waiting for your OK on …". */
export function activityLabel(t) {
    const s = t.salient ? `: ${t.salient}` : "";
    if (t.status === "awaiting_approval")
        return `Waiting for your OK on ${t.name}`;
    if (/search/i.test(t.name))
        return `Searching${s}`;
    if (/^(bash|shell)$|shellcommand/i.test(t.name))
        return `Running${s}`;
    if (/^(write|edit)$/i.test(t.name))
        return `Writing${s}`;
    if (/^(read|glob|grep|ls)$/i.test(t.name))
        return `Reading${s}`;
    if (/fetch/i.test(t.name))
        return `Fetching${s}`;
    return `Using ${t.name}${s}`;
}
// ── fold internals ─────────────────────────────────────────────────────────────────────────
/** Bounds on the fold's two id lists (most recent kept): copies arrive close behind their originals. */
const MAX_TOKEN_KEYS = 256;
const MAX_DELEGATED = 256;
const STATE_LABEL = {
    not_started: "Not started",
    working: "Working",
    waiting: "Needs you",
    idle: "Idle",
    done: "Finished",
    error: "Stopped on an error",
    stopped: "Stopped",
    paused: "Paused at the budget",
};
/** Rows that show a thread still at work after an interrupt reached it. */
const THREAD_WORK = new Set([
    "session.thread_status_running",
    "agent.message",
    "agent.thinking",
    "agent.tool_use",
    "agent.mcp_tool_use",
    "agent.custom_tool_use",
    "agent.thread_message_sent",
]);
function noEffects() {
    return { closed: [], opened: [], header: {} };
}
function readable(e) {
    return Boolean(e) && typeof e?.id === "string" && typeof e.type === "string";
}
/** A working copy: every array and object the fold writes is copied, so the view given is never changed. */
function draft(v) {
    return {
        ...v,
        followUp: { ...v.followUp },
        capabilities: { ...v.capabilities },
        session: { ...v.session },
        agents: v.agents.slice(),
        workflows: v.workflows.slice(),
        asks: v.asks.slice(),
        queued: v.queued.slice(),
        totals: { ...v.totals },
        outputs: { ...v.outputs },
        fold: { ...v.fold },
    };
}
/** The derived fields, from the stored ones: totals, mode, needs, the budget gate, ask coverage. */
function settle(d) {
    d.totals = {
        tokens: d.agents.reduce((n, a) => n + a.tokens, 0),
        graderTokens: d.totals.graderTokens,
        costCents: d.usage ? d.usage.totalCostCents : null,
        costState: d.usage ? d.usage.state : "unknown",
        helpers: d.agents.filter((a) => a.role !== "lead").length,
        working: d.agents.filter((a) => a.state === "working").length,
        waiting: d.agents.filter((a) => a.state === "waiting").length,
        searches: d.totals.searches,
    };
    const grants = d.toolGrants;
    if (d.asks.some((a) => coverOf(a, grants) !== a.coveredBy))
        d.asks = d.asks.map((a) => {
            const coveredBy = coverOf(a, grants);
            return coveredBy === a.coveredBy ? a : { ...a, coveredBy };
        });
    d.mode = modeOf(d);
    d.needs = needsOf(d);
    d.capabilities.canRaiseBudget =
        d.budget !== null && d.budget.capCents < d.budget.maxCents && !isTerminalRunStatus(d.status);
    return d;
}
/** An ask an Always allow this run holds covers is the server's to answer. (The toolbelt's is the server's call: runs.view.) */
function coverOf(a, grants) {
    if (a.coveredBy === "toolbelt")
        return "toolbelt";
    return a.grantKey !== null && grants.includes(a.grantKey) ? "grant" : null;
}
/** The console's mode (its own deriveConsoleMode until H1b.10b read this), from the view's own fields. */
function modeOf(v) {
    if (!isTerminalRunStatus(v.status)) {
        if (v.status === "preparing" && !v.capabilities.canSend)
            return "preparing";
        if (v.asks.length > 0 || v.status === "requires_action")
            return "needs-you";
        return "working";
    }
    if (v.session.state === "unknown")
        return "finishing";
    if (v.session.state === "none" || v.session.state === "terminated")
        return "terminated";
    if (v.session.resumeReady && v.capabilities.canFollowUp)
        return "completed-resumable";
    if (v.outputs.finalization !== "complete" && v.outputs.finalization !== "failed")
        return "finishing";
    return "completed";
}
/** What the person is waited on for: an ask (a question first), the budget, the runs after an interrupt, the per-minute limit. */
function needsOf(v) {
    const asks = isTerminalRunStatus(v.status) ? [] : v.asks;
    if (asks.some((a) => a.kind === "question"))
        return "question";
    if (asks.length > 0)
        return "permission";
    if (v.status === "requires_action" && v.stopReason === "budget_reached")
        return "budget";
    if (v.mode === "needs-you" &&
        v.status === "requires_action" &&
        v.fold.leadInterrupted &&
        v.fold.runsInTurn)
        return "workflow_runs";
    if (v.fold.rateLimited.length > 0 &&
        (v.mode === "working" || v.mode === "needs-you" || v.mode === "completed-resumable"))
        return "rate_limit";
    return null;
}
function apply(d, e, fx) {
    const f = d.fold;
    if (e.runId && f.lastRunId && e.runId !== f.lastRunId)
        f.interrupted = [];
    if (e.runId)
        f.lastRunId = e.runId;
    const current = !e.runId || e.runId === d.runId;
    if (current) {
        trackQueued(d, e);
        trackRateLimit(d, e);
        trackTurnFacts(d, e);
    }
    if (!isKnownRunEvent(e)) {
        applyUnknown(d, e, fx);
        return;
    }
    if (f.interrupted.length && THREAD_WORK.has(e.type)) {
        const t = threadOf(e);
        if (t)
            dropInterrupted(d, t); // still working after the interrupt: it was not stopped
    }
    const at = e.occurredAt;
    switch (e.type) {
        // ── the lead's session lifecycle (untagged) ──
        case "session.status_running": {
            if (current && freshLifecycle(d, e)) {
                header(d, fx, { status: "running", stopping: false });
                f.idleReason = null;
                patchAgent(d, "lead", {
                    state: hasAsk(d, "lead") ? "waiting" : "working",
                    endedAt: null,
                });
            }
            return;
        }
        case "session.status_idle": {
            nameAsks(d, e.payload);
            const reason = stopReasonOf(e.payload);
            // The run service's rule (run-status.ts statusAtIdle) on this run's facts: a workflow run
            // still open, or an interrupt in a turn that had one, means the lead's turn ended and the
            // work did not; else the pause, the interrupt, the per-minute limit, the stop reason, the
            // turn's verdict and the turn's error, in that order.
            const ending = statusAtIdle(turnFactsOf(d), reason);
            fx.idleStatus = ending.status;
            if (current && freshLifecycle(d, e)) {
                header(d, fx, { status: ending.status, stopReason: ending.stopReason, stopping: false });
                f.idleReason = reason;
                patchAgent(d, "lead", { state: agentStateFromStop(reason) });
                // A workflow run's thread keeps its asks: the client must still answer them.
                if (!isPauseStop(reason))
                    cancelAsks(d, fx, { keepRunThreads: true });
            }
            return;
        }
        case "session.status_terminated": {
            if (current && freshLifecycle(d, e)) {
                // As the run service records it (runs.ts: failed, "terminated"); the idle before it no
                // longer names the stop (hunt 2026-10-27 #60: a budget pause still offered "Add $1").
                header(d, fx, { status: "failed", stopReason: "terminated", stopping: false });
                f.idleReason = null;
                patchAgent(d, "lead", { state: "done", endedAt: at });
                cancelAsks(d, fx);
            }
            return;
        }
        case "session.status_rescheduled":
            return;
        case "session.error": {
            const p = e.payload;
            const rs = p.error?.retry_status?.type ?? "";
            if (/exhaust/i.test(rs))
                patchAgent(d, laneOf(d, p.session_thread_id), { state: "error" });
            return;
        }
        // ── the roster and thread lifecycle ──
        case "session.thread_created": {
            const p = e.payload;
            const t = p.session_thread_id;
            ensureHelper(d, t, p.agent_name, at);
            patchAgent(d, t, (a) => ({
                state: a.state === "not_started" ? "working" : a.state,
                startedAt: a.startedAt ?? at,
                workflowRunId: p.workflow_run_id ?? a.workflowRunId,
            }));
            // A run's threads are the ones whose thread_created names the run (never a phase).
            if (p.workflow_run_id)
                attachToRun(d, p.workflow_run_id, t);
            return;
        }
        case "session.thread_status_running":
        case "session.thread_status_idle":
        case "session.thread_status_terminated":
        case "session.thread_status_rescheduled": {
            if (e.type === "session.thread_status_idle")
                nameAsks(d, e.payload);
            const key = laneOf(d, e.payload.session_thread_id);
            if (key === "lead")
                return; // the primary narrating itself (Claude); the lead follows session.status_*
            const ended = e.type === "session.thread_status_terminated";
            const idle = e.type === "session.thread_status_idle";
            // A terminated thread takes no answer (the server says command_target_closed): its open asks are closed.
            if (ended)
                for (const a of [...d.asks, ...(d.fold.cutAsks ?? [])].filter((ask) => laneOfAsk(ask) === key)) {
                    closeAsk(d, fx, a.id, "closed", {}, false);
                    settleAsk(d, a.id, "closed");
                }
            const interrupted = (idle || ended) && dropInterrupted(d, key);
            let state = idle
                ? agentStateFromStop(e.payload.stop_reason?.type ?? "end_turn")
                : ended
                    ? "done"
                    : "working";
            if (state === "working" && hasAsk(d, key))
                state = "waiting";
            // An interrupted child idles end_turn (or just ends): its turn was stopped, it did not finish.
            if (interrupted && (state === "idle" || state === "done"))
                state = "stopped";
            patchAgent(d, key, (a) => {
                // Terminated after an error or a stop keeps that state: the thread ended, but not by finishing.
                const next = ended && (a.state === "error" || a.state === "stopped") ? a.state : state;
                return {
                    state: next,
                    terminated: ended,
                    endedAt: ended ? at : next === "working" ? null : a.endedAt,
                    activity: next === "working" ? a.activity : { at, label: STATE_LABEL[next] },
                };
            });
            return;
        }
        // ── delegation ──
        case "agent.thread_message_sent": {
            const p = e.payload;
            const from = laneOf(d, p.session_thread_id);
            const to = resolveAgent(d, p.to_session_thread_id, p.to_agent_name, from === "lead", at);
            fx.lanes = { at: from, from, to };
            if (from !== "lead" && to === "lead") {
                touch(d, from, at, "Reported back");
                return; // Claude's helper-side copy of a report; the lead-side `received` row carries it
            }
            if (from === to)
                return;
            if (from === "lead")
                rememberDelegation(d, `${to}|${textOf(p.content)}`);
            touch(d, from, at, `Asked ${agentOf(d, to).label}`);
            touch(d, to, at, "Got a task");
            return;
        }
        case "agent.thread_message_received": {
            const p = e.payload;
            const lane = laneOf(d, p.session_thread_id);
            if (lane !== "lead") {
                touch(d, lane, at, "Got a task");
                // Claude logs a free-form delegation twice (the lead's `sent` row first), but a
                // workflow run logs only this helper-side copy: draw it once, from whichever lands first.
                const from = resolveAgent(d, p.from_session_thread_id, p.from_agent_name, false, at);
                fx.lanes = { at: lane, from, to: lane };
                const key = `${lane}|${textOf(p.content)}`;
                if (from !== "lead" || hasDelegation(d, key)) {
                    fx.draw = null;
                    return;
                }
                rememberDelegation(d, key);
                const target = agentOf(d, lane);
                if (!p.from_agent_name && target.workflowRunId) {
                    // A run thread's prompt with no sender is the workflow's own, not the lead's.
                    fx.draw = "workflow";
                    return;
                }
                fx.draw = "lead";
                touch(d, "lead", at, `Asked ${target.label}`);
                return;
            }
            const from = resolveAgent(d, p.from_session_thread_id, p.from_agent_name, true, at);
            fx.lanes = { at: "lead", from, to: "lead" };
            if (from === "lead")
                return;
            patchAgent(d, from, (a) => ({
                reports: a.reports + 1,
                activity: { at, label: "Reported back" },
            }));
            return;
        }
        // ── work, in the agent's lane ──
        case "agent.message": {
            const key = laneOf(d, e.payload.session_thread_id);
            touch(d, key, at, textOf(e.payload.content) ? "Wrote a message" : "Replied (withheld)");
            return;
        }
        case "agent.thinking":
            touch(d, laneOf(d, e.payload.session_thread_id), at, "Thinking");
            return;
        case "agent.tool_use":
        case "agent.mcp_tool_use": {
            const p = e.payload;
            if (isGraderThread(d, p.session_thread_id))
                return; // the grader's call: no activity, no ask
            const key = laneOf(d, p.session_thread_id);
            const agent = agentOf(d, key);
            const callId = p.id || e.id;
            const input = isRecord(p.input) ? p.input : {};
            const ask = p.evaluated_permission === "ask";
            const salient = salientOf(input);
            if (/search/i.test(p.name))
                d.totals.searches += 1;
            touch(d, key, at, activityLabel({ name: p.name, salient, status: ask ? "awaiting_approval" : "running" }));
            if (ask) {
                const subject = toolGrantSubjectOf({ type: e.type, name: p.name, mcp_server_name: p.mcp_server_name }, key === "lead" ? d.leadName : agent.name);
                openAsk(d, fx, {
                    id: callId,
                    kind: "permission",
                    thread: key === "lead" ? null : key,
                    agentName: agent.label,
                    at,
                    tool: p.name,
                    mcpServer: p.mcp_server_name ?? null,
                    preview: salient ?? null,
                    input,
                    question: null,
                    context: null,
                    responseMode: null,
                    choices: null,
                    quickReplies: null,
                    canAlwaysAllow: d.capabilities.canAlwaysAllow,
                    coveredBy: null,
                    grantKey: subject ? toolGrantKey(subject) : null,
                    named: false,
                });
            }
            return;
        }
        case "agent.custom_tool_use": {
            const p = e.payload;
            const key = laneOf(d, p.session_thread_id);
            const agent = agentOf(d, key);
            const q = p.input;
            touch(d, key, at, "Asked you a question");
            openAsk(d, fx, {
                id: p.id || e.id,
                kind: "question",
                thread: key === "lead" ? null : key,
                agentName: agent.label,
                at,
                tool: null,
                mcpServer: null,
                preview: null,
                input: null,
                question: q.question,
                context: q.context ?? null,
                responseMode: q.responseMode ?? null,
                choices: q.choices ?? null,
                quickReplies: q.quickReplies ?? null,
                canAlwaysAllow: false,
                coveredBy: null,
                grantKey: null,
                named: false,
            });
            return;
        }
        case "agent.tool_result":
        case "agent.mcp_tool_result": {
            const p = e.payload;
            const key = laneOf(d, p.session_thread_id);
            const graderCall = isGraderThread(d, p.session_thread_id);
            const closedByPlatform = p.is_error || INTERRUPTED_CALL.test(contentText(p.content));
            // A result ends the call of either kind, whatever came before it: the server reads the
            // ask it ends as answered, one its thread's end closed included.
            const ends = resultIds(p).filter((id) => askOf(d, id) !== undefined || settledOf(d, id) === "closed");
            for (const id of ends)
                settleAsk(d, id, "answered");
            if (e.type === "agent.tool_result" && e.payload.custom_tool_use_id) {
                const id = e.payload.custom_tool_use_id;
                if (closedByPlatform)
                    closeAsk(d, fx, id, "closed");
                else
                    closeAsk(d, fx, id, "answered", { answer: contentText(p.content) });
                return;
            }
            const callId = e.type === "agent.mcp_tool_result"
                ? e.payload.mcp_tool_use_id || str(e.payload.tool_use_id)
                : e.payload.tool_use_id;
            // No decision on record: a clean result means it ran (allowed); an error or the
            // interrupt text means the platform closed the call, which nobody allowed.
            if (callId && askOf(d, callId)?.kind === "permission")
                closeAsk(d, fx, callId, closedByPlatform ? "closed" : "allowed");
            if (!graderCall)
                touch(d, key, at, p.is_error ? "A tool failed" : "Got a tool result");
            return;
        }
        case "agent.thread_context_compacted":
            return;
        // ── the person ──
        case "user.message":
            if (current) {
                f.leadInterrupted = false;
                f.runsInTurn = f.openRuns.length > 0;
            }
            return;
        case "user.define_outcome":
            if (current) {
                // The person's outcome counts as their message does: the next idle can settle the run.
                f.leadInterrupted = false;
                f.runsInTurn = f.openRuns.length > 0;
            }
            return;
        case "user.tool_confirmation": {
            const p = e.payload;
            // A reply answers a call of its own kind only: a confirmation never answers a question.
            if (askOf(d, p.tool_use_id)?.kind === "question")
                return;
            const closed = closeAsk(d, fx, p.tool_use_id, p.result === "allow" ? "allowed" : "denied", p.deny_message ? { denyMessage: p.deny_message } : {});
            if (closed)
                settleAsk(d, closed.id, "answered");
            return;
        }
        case "user.custom_tool_result": {
            const id = e.payload.custom_tool_use_id;
            // A custom tool result never answers a tool call that carries the same id.
            if (askOf(d, id)?.kind === "permission")
                return;
            // An error result is a cancel (or the platform's), not an answer: the ask reads Closed.
            // Either way it is the question's reply, which the server's ledger reads as answered.
            const closed = e.payload.is_error
                ? closeAsk(d, fx, id, "closed")
                : closeAsk(d, fx, id, "answered", { answer: textOf(e.payload.content) });
            if (closed)
                settleAsk(d, closed.id, "answered");
            return;
        }
        case "user.interrupt": {
            // B5: one naming a child thread stopped that thread only; the lead and the run go on. The
            // contract's user.interrupt payload has no field, so the tag is a stored extra (threadOf).
            const thread = laneOf(d, threadOf(e));
            if (thread !== "lead") {
                const agent = agentOf(d, thread);
                // It cuts that thread's asks, unless the thread is a workflow run's (the client must
                // still answer those).
                if (!agent.workflowRunId)
                    cutAsks(d, (a) => laneOfAsk(a) === thread);
                if (current && (agent.state === "working" || agent.state === "waiting"))
                    addInterrupted(d, thread);
                return;
            }
            // The asks it reached are cut, a workflow run's thread's aside: nothing answers them now.
            cutAsks(d, (a) => !onRunThread(d, a));
            if (current) {
                header(d, fx, { stopping: true });
                f.leadInterrupted = true;
                for (const a of d.agents)
                    if (a.thread && (a.state === "working" || a.state === "waiting" || a.state === "idle"))
                        addInterrupted(d, a.key);
            }
            return;
        }
        // ── workflows ──
        case "workflow_run.created": {
            const p = e.payload;
            const listed = p.phases.map((ph) => ({
                id: ph.id,
                name: ph.name,
                state: "todo",
                unlisted: false,
            }));
            const i = d.workflows.findIndex((w) => w.id === p.workflow_run_id);
            const prior = d.workflows[i];
            if (prior)
                // A row an earlier event drew by the run's id: the plan names it now. What the run's
                // own rows already said (its state, phases, threads, result) stands.
                d.workflows[i] = {
                    ...prior,
                    name: p.name,
                    description: p.description,
                    planUnknown: false,
                    phases: [
                        ...listed.map((ph) => {
                            const known = prior.phases.find((x) => x.id === ph.id);
                            return known ? { ...ph, state: known.state } : ph;
                        }),
                        ...prior.phases
                            .filter((x) => !listed.some((ph) => ph.id === x.id))
                            .map((x) => ({ ...x, unlisted: true })),
                    ],
                };
            else
                d.workflows.push({
                    id: p.workflow_run_id,
                    name: p.name,
                    description: p.description,
                    state: "planned",
                    phases: listed,
                    threads: [],
                    planUnknown: false,
                    result: null,
                    error: null,
                    errorType: null,
                    costCents: null,
                });
            if (current && p.workflow_run_id && !f.openRuns.includes(p.workflow_run_id)) {
                f.openRuns = [...f.openRuns, p.workflow_run_id];
                f.runsInTurn = true;
                // A run started while the lead idles: the idle reads again with the run open.
                if (f.idleReason !== null) {
                    const ending = statusAtIdle(turnFactsOf(d), f.idleReason);
                    header(d, fx, { status: ending.status, stopReason: ending.stopReason });
                }
            }
            return;
        }
        case "workflow_run.status_running":
        case "workflow_run.status_idle": {
            const next = e.type === "workflow_run.status_running" ? "running" : "idle";
            ensureWorkflow(d, e.payload.workflow_run_id);
            patchWorkflow(d, e.payload.workflow_run_id, (w) => ({
                state: w.state === "ended" ? w.state : next,
            }));
            return;
        }
        case "workflow_run.status_ended": {
            const p = e.payload;
            const type = workflowOutcomeOf(p.result);
            const err = p.result?.type === "error" && isRecord(p.result.error) ? p.result.error : undefined;
            // A run this view never saw created still gets its row, named by its id, with its result.
            ensureWorkflow(d, p.workflow_run_id);
            patchWorkflow(d, p.workflow_run_id, (w) => ({
                state: "ended",
                result: type,
                error: str(err?.message) ?? null,
                errorType: str(err?.type) ?? null,
                phases: w.phases.map((ph) => ph.state === "running"
                    ? { ...ph, state: type === "stopped" || type === "error" ? "failed" : "done" }
                    : ph),
            }));
            f.openPhases = f.openPhases.filter((o) => o.workflowId !== p.workflow_run_id);
            if (current && f.openRuns.includes(p.workflow_run_id))
                f.openRuns = f.openRuns.filter((id) => id !== p.workflow_run_id);
            // The run's end settles nothing, even the last one while the lead idles: the lead gets a
            // turn to read it, and that turn's idle decides (the Workflow runs page's done rule).
            return;
        }
        case "workflow_run.error": {
            const p = e.payload;
            if (p.workflow_run_id) {
                ensureWorkflow(d, p.workflow_run_id);
                patchWorkflow(d, p.workflow_run_id, () => ({
                    error: typeof p.error.message === "string" ? p.error.message : null,
                    errorType: typeof p.error.type === "string" ? p.error.type : null,
                }));
            }
            return;
        }
        // Phase rows never change the run's own state (a paused run stays paused); only status_* do.
        case "workflow_run.phase_started": {
            const p = e.payload;
            const wid = p.workflow_run_id;
            const pid = p.workflow_run_phase_id;
            const own = str(e.payload.id);
            openPhase(d, own && own !== e.id ? [e.id, own] : [e.id], wid, pid);
            ensureWorkflow(d, wid);
            patchWorkflow(d, wid, (w) => ({ phases: withPhase(w.phases, pid, "running") }));
            return;
        }
        case "workflow_run.phase_ended": {
            const p = e.payload;
            // Match the end to its start by phase_started_id; the phase id is the fallback (the loop
            // invents phase_started_id).
            const byStart = p.phase_started_id
                ? f.openPhases.find((o) => o.ids.includes(p.phase_started_id))
                : undefined;
            const match = byStart ??
                f.openPhases.find((o) => o.workflowId === p.workflow_run_id && o.phaseId === p.workflow_run_phase_id);
            const wid = match?.workflowId ?? p.workflow_run_id;
            const pid = match?.phaseId ?? p.workflow_run_phase_id;
            if (match)
                f.openPhases = f.openPhases.filter((o) => o !== match);
            const stillOpen = f.openPhases.some((o) => o.workflowId === wid && o.phaseId === pid);
            ensureWorkflow(d, wid);
            // An end whose start this view never saw still marks the phase done (added by its id when
            // the plan does not list it).
            if (!stillOpen)
                patchWorkflow(d, wid, (w) => ({ phases: withPhase(w.phases, pid, "done") }));
            return;
        }
        // ── measurements and grading ──
        case "span.model_request_end": {
            const p = e.payload;
            const dedupe = hashKey(p.model_request_start_id || e.id);
            if (f.tokenKeys.includes(dedupe) || !p.model_usage) {
                fx.counted = null;
                return;
            }
            f.tokenKeys = bounded(f.tokenKeys, dedupe, MAX_TOKEN_KEYS);
            const u = p.model_usage;
            patchAgent(d, laneOf(d, p.session_thread_id), (a) => ({
                tokens: a.tokens + u.output_tokens,
                usage: addUsage(a.usage, u),
            }));
            fx.counted = u;
            return;
        }
        case "span.outcome_evaluation_start":
        case "span.outcome_evaluation_ongoing":
        case "span.outcome_evaluation_end": {
            f.grading = e.type !== "span.outcome_evaluation_end";
            if (e.type === "span.outcome_evaluation_end") {
                // The verdict ends the iteration it graded: an ask from before it is cut, a workflow
                // run's thread's aside.
                cutAsks(d, (a) => !onRunThread(d, a));
                d.outcome = e.payload.result;
                d.totals.graderTokens += e.payload.usage?.output_tokens ?? 0;
            }
            return;
        }
    }
}
function applyUnknown(d, e, fx) {
    const p = isRecord(e.payload) ? e.payload : {};
    if (e.type === "session.usage") {
        // B6. Per-thread spend, if a future stream ever carries session.usage (runs.get groups it today).
        const usage = isRecord(p.usage) ? p.usage : {};
        const rows = Array.isArray(p.x_threads)
            ? p.x_threads
            : Array.isArray(usage.x_threads)
                ? usage.x_threads
                : [];
        for (const raw of rows) {
            if (!isRecord(raw) || raw.list_cost_cents == null || raw.kind === "grader")
                continue;
            patchAgent(d, raw.thread ? laneOf(d, raw.thread) : "lead", {
                costCents: raw.list_cost_cents,
            });
        }
    }
    else if (e.type === "status" && isRunStatus(p.status) && (!e.runId || e.runId === d.runId)) {
        header(d, fx, { status: p.status }); // the proxy status projection: never sent by the V5 server
    }
}
function header(d, fx, patch) {
    if (patch.status !== undefined)
        d.status = patch.status;
    if (patch.stopReason !== undefined)
        d.stopReason = patch.stopReason;
    if (patch.stopping !== undefined)
        d.stopping = patch.stopping;
    Object.assign(fx.header, patch);
}
/**
 * The turn count, after one envelope of the log (`prev`: the view before it). A follow-up turn
 * begins at the rows of another run (the feed's divider has always marked that), or at the
 * run's own user.message once the log settled the run: the server's runs.command takes a
 * message to a run whose status is terminal as its next turn. Only the log's own status rows
 * decide that a turn ended, never the run row, which reads running before the follow-up's
 * message is stored and may be terminal before the first is. A status row that leaves the run
 * live reopens the turn, but for the session's running row: Claude logs it before the message
 * that woke the session, so the follow-up's message after it still opens the next turn.
 */
function countTurn(prev, d, e, fx) {
    const f = d.fold;
    const otherRun = Boolean(e.runId && prev.fold.lastRunId && e.runId !== prev.fold.lastRunId);
    const current = !e.runId || e.runId === d.runId;
    if (otherRun || (current && e.type === "user.message" && prev.fold.turnEnded === true)) {
        d.turn = (prev.turn ?? 1) + 1;
        f.turnEnded = false;
        fx.turn = d.turn;
    }
    const status = current ? fx.header.status : undefined;
    if (status === undefined)
        return;
    if (isTerminalRunStatus(status))
        f.turnEnded = true;
    else if (e.type !== "session.status_running")
        f.turnEnded = false;
}
/** A user event logged with processed_at null is queued until its processed copy arrives. */
function trackQueued(d, e) {
    if (!e.type.startsWith("user."))
        return;
    const processed = processedAtOf(e);
    if (processed === null) {
        if (!d.queued.includes(e.id))
            d.queued = [...d.queued, e.id];
    }
    else if (typeof processed === "string" && d.queued.includes(e.id)) {
        d.queued = d.queued.filter((id) => id !== e.id);
    }
}
/** The row's processed marker: null while Claude still queues it, its time once processed, undefined when the row has none. */
function processedAtOf(e) {
    const processed = e.payload?.processed_at;
    return processed === null || typeof processed === "string" ? processed : undefined;
}
/**
 * An envelope at a sequence the view has folded: the view itself, unless it is the processed
 * copy of a user event the view holds queued (the server's in-place fill). Then the event leaves
 * `queued`, which is all the view keeps of a user row's processed state: the row's turn effects
 * were applied when it was first folded, and nothing is replayed. The queued copy again, or the
 * processed copy once applied, changes nothing.
 */
function filledInPlace(view, e) {
    if (e.runId && e.runId !== view.runId)
        return view;
    if (!view.queued.includes(e.id) || typeof processedAtOf(e) !== "string")
        return view;
    return { ...view, queued: view.queued.filter((id) => id !== e.id) };
}
/**
 * The follow-up window after a run row, field by field: an explicit null clears, a field left out
 * keeps. While the row says nothing of `open` (runs.get does not carry followUp yet), the window
 * is open when the row's canResume says so; a window open has no reason it closed.
 */
function followUpOf(prev, row) {
    const given = row.followUp;
    const open = given?.open ?? row.canResume ?? prev.open;
    return {
        open,
        closedReason: given?.closedReason !== undefined ? given.closedReason : open ? null : prev.closedReason,
        until: given?.until !== undefined ? given.until : prev.until,
    };
}
/** The organization's per-minute limit (run-events.ts trackOrgRateLimit), by thread, with when it stopped each. */
function trackRateLimit(d, e) {
    const f = d.fold;
    const stopped = new Set(f.rateLimited.map((r) => r.thread));
    const thread = trackOrgRateLimit(stopped, e.type, e.payload);
    if (thread !== null) {
        f.rateLimited = f.rateLimited.some((r) => r.thread === thread)
            ? f.rateLimited.map((r) => (r.thread === thread ? { thread, at: e.occurredAt } : r))
            : [...f.rateLimited, { thread, at: e.occurredAt }];
    }
    else if (stopped.size !== f.rateLimited.length) {
        f.rateLimited = f.rateLimited.filter((r) => stopped.has(r.thread));
    }
}
function isRunStatus(v) {
    return RUN_STATUS_LIST.includes(v);
}
function firstLine(mission) {
    return mission?.split("\n")[0]?.trim() || "";
}
function workflowOutcomeOf(result) {
    const raw = result?.type;
    // A result type outside the three the docs list is a run that ended some other way.
    return raw === "completed" || raw === "stopped" || raw === "error" ? raw : "other";
}
function leadStateFromStatus(s, stopReason) {
    if (s === "preparing")
        return "not_started";
    if (s === "running")
        return "working";
    if (s === "requires_action")
        return stopReason === "budget_reached" ? "paused" : "waiting";
    if (s === "interrupted")
        return "stopped";
    return s === "failed" ? "error" : "idle";
}
function zeroUsage() {
    return { input: 0, output: 0, cacheRead: 0, cacheCreation: 0 };
}
function addUsage(t, u) {
    return {
        input: t.input + (u.input_tokens ?? 0),
        output: t.output + (u.output_tokens ?? 0),
        cacheRead: t.cacheRead + (u.cache_read_input_tokens ?? 0),
        cacheCreation: t.cacheCreation + (u.cache_creation_input_tokens ?? 0),
    };
}
/** A short, stable hash (cyrb53), so the fold's id lists stay small on the wire. */
function hashKey(s) {
    let h1 = 0xdeadbeef;
    let h2 = 0x41c6ce57;
    for (let i = 0; i < s.length; i++) {
        const ch = s.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
    h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}
function bounded(list, item, max) {
    return [...(list.length >= max ? list.slice(list.length - max + 1) : list), item];
}
function rememberDelegation(d, key) {
    const h = hashKey(key);
    if (!d.fold.delegated.includes(h))
        d.fold.delegated = bounded(d.fold.delegated, h, MAX_DELEGATED);
}
function hasDelegation(d, key) {
    return d.fold.delegated.includes(hashKey(key));
}
function addInterrupted(d, thread) {
    if (!d.fold.interrupted.includes(thread))
        d.fold.interrupted = [...d.fold.interrupted, thread];
}
/** Takes a thread off the interrupted list; true when it was on it. */
function dropInterrupted(d, thread) {
    if (!d.fold.interrupted.includes(thread))
        return false;
    d.fold.interrupted = d.fold.interrupted.filter((t) => t !== thread);
    return true;
}
/** Records an open phase under each of its start's ids; an id another open phase held moves to this one. */
function openPhase(d, ids, workflowId, phaseId) {
    const rest = d.fold.openPhases
        .map((o) => ({ ...o, ids: o.ids.filter((id) => !ids.includes(id)) }))
        .filter((o) => o.ids.length > 0);
    d.fold.openPhases = [...rest, { ids, workflowId, phaseId }];
}
function setSeat(d, info) {
    const i = d.fold.seats.findIndex((s) => s.name === info.name);
    d.fold.seats = i < 0 ? [...d.fold.seats, info] : d.fold.seats.map((s, j) => (j === i ? info : s));
}
/** The threads a session.thread_created introduced are the agents' threads. */
function created(v, thread) {
    return v.agents.some((a) => a.thread === thread);
}
function patchAgent(d, key, patch) {
    const i = d.agents.findIndex((a) => a.key === key);
    const a = d.agents[i];
    if (a)
        d.agents[i] = { ...a, ...(typeof patch === "function" ? patch(a) : patch) };
}
function touch(d, key, at, label) {
    patchAgent(d, key, { activity: { at, label } });
}
function hasAsk(d, key) {
    return d.asks.some((a) => laneOfAsk(a) === key);
}
function renameLead(d, name) {
    patchAgent(d, "lead", { name, label: name });
}
function displayName(name) {
    return name === ADVISOR_AGENT_NAME ? "Advisor" : name;
}
function aliasOf(thread) {
    return thread && /^t[1-9]\d*$/.test(thread) ? thread : null;
}
function newAgent(d, key, thread, name, role) {
    const base = displayName(name);
    const same = d.agents.filter((a) => displayName(a.name) === base).length;
    const seat = d.fold.seats.find((s) => s.name === name);
    return {
        key,
        thread,
        alias: aliasOf(thread),
        name,
        label: same ? `${base} ${same + 1}` : base,
        laneIndex: d.agents.filter((a) => a.role !== "lead").length + 1,
        role,
        state: "not_started",
        model: seat?.model ?? null,
        vendor: seat?.vendor ?? null,
        provider: seat?.provider ?? null,
        served: seat?.served ?? null,
        substituted: seat?.substituted ?? null,
        tokens: 0,
        usage: zeroUsage(),
        costCents: null,
        startedAt: null,
        endedAt: null,
        terminated: null,
        workflowRunId: null,
        activity: null,
        reports: 0,
    };
}
/** Creates a helper card on first sight, claiming an unspawned seat card of the same name when there is one. */
function ensureHelper(d, thread, name, at) {
    if (d.agents.some((a) => a.key === thread))
        return;
    const role = name === ADVISOR_AGENT_NAME ? "advisor" : "helper";
    const seat = d.agents.findIndex((a) => a.thread === null &&
        a.key !== "lead" &&
        a.state === "not_started" &&
        (a.name === name || (role === "advisor" && a.role === "advisor")));
    const claimed = d.agents[seat];
    if (claimed) {
        d.agents[seat] = {
            ...claimed,
            key: thread,
            thread,
            name,
            label: name === claimed.name ? claimed.label : displayName(name),
            alias: aliasOf(thread),
            startedAt: at ?? null,
        };
    }
    else {
        d.agents.push({ ...newAgent(d, thread, thread, name, role), startedAt: at ?? null });
    }
}
/** A delegation's other end. An unknown thread named by the lead's own row is a helper seen first here. */
function resolveAgent(d, thread, name, leadSide, at) {
    if (thread && created(d, thread))
        return thread;
    if (thread && name && leadSide) {
        ensureHelper(d, thread, name, at);
        return thread;
    }
    if (!thread && name) {
        const byName = [...d.agents].reverse().find((a) => a.key !== "lead" && a.name === name);
        if (byName)
            return byName.key;
    }
    return "lead";
}
/** Files a thread under its run (thread_created's workflow_run_id), never under a phase. */
function attachToRun(d, workflowId, thread) {
    ensureWorkflow(d, workflowId);
    patchWorkflow(d, workflowId, (w) => w.threads.includes(thread) ? {} : { threads: [...w.threads, thread] });
}
/**
 * The row of a run this view has not seen created: any of its rows (or a thread's) proves the run
 * exists, so it gets a row named by its id until workflow_run.created names it. With no status
 * row yet it is `planned` (not started to execute), and it counts as open until its status_ended.
 */
function ensureWorkflow(d, id) {
    if (typeof id !== "string" || !id || d.workflows.some((w) => w.id === id))
        return;
    d.workflows.push({
        id,
        name: id,
        description: null,
        state: "planned",
        phases: [],
        threads: [],
        planUnknown: true,
        result: null,
        error: null,
        errorType: null,
        costCents: null,
    });
}
/** Sets a phase's state, adding it by its id (unlisted) when the bar does not have it. */
function withPhase(phases, id, state) {
    return phases.some((ph) => ph.id === id)
        ? phases.map((ph) => (ph.id === id ? { ...ph, state } : ph))
        : [...phases, { id, name: id, state, unlisted: true }];
}
function patchWorkflow(d, id, patch) {
    const i = d.workflows.findIndex((w) => w.id === id);
    const w = d.workflows[i];
    if (w)
        d.workflows[i] = { ...w, ...patch(w) };
}
/** The done rule's facts of this run (run-status.ts TurnFacts), as the server keeps them from its log. */
function turnFactsOf(d) {
    const f = d.fold;
    return {
        interrupted: f.leadInterrupted,
        runsInTurn: f.runsInTurn,
        openRuns: { size: f.openRuns.length },
        rateLimited: { size: f.rateLimited.length },
        lastOutcome: f.verdict,
        turnError: f.turnError,
    };
}
/**
 * The turn's verdict and error from this run's own rows (run-status.ts trackTurn): the person's
 * message clears both; a grader's verdict sets the one, a lead's error the turn did not recover
 * from the other (turnErrorAfter).
 */
function trackTurnFacts(d, e) {
    const f = d.fold;
    if (e.type === "user.message" || e.type === "user.define_outcome")
        f.verdict = null;
    if (e.type === "span.outcome_evaluation_end") {
        const result = e.payload?.result;
        if (typeof result === "string")
            f.verdict = result;
    }
    f.turnError = turnErrorAfter(f.turnError, e.type, e.payload);
}
/** Lifecycle rows older than the latest applied one never regress the status (use-run-stream.ts:106-117). */
function freshLifecycle(d, e) {
    const prev = d.fold.lifecycle;
    const t = Date.parse(e.occurredAt);
    if (prev) {
        const pt = Date.parse(prev.at);
        const after = Number.isFinite(t) && Number.isFinite(pt) && t !== pt ? t > pt : e.sequence > prev.seq;
        if (!after)
            return false;
    }
    d.fold.lifecycle = { at: e.occurredAt, seq: e.sequence };
    return true;
}
function openAsk(d, fx, ask) {
    // A copy of an ask an idle already named is still named.
    const named = ask.named || openAskOf(d, ask.id)?.named === true;
    d.asks = [...d.asks.filter((a) => a.id !== ask.id), { ...ask, named }];
    if (cutAskOf(d, ask.id))
        d.fold.cutAsks = (d.fold.cutAsks ?? []).filter((c) => c.id !== ask.id);
    fx.opened.push(ask.id);
    patchAgent(d, laneOfAsk(ask), { state: "waiting" });
}
function openAskOf(d, id) {
    return id ? d.asks.find((a) => a.id === id) : undefined;
}
function cutAskOf(d, id) {
    return id ? d.fold.cutAsks?.find((c) => c.id === id) : undefined;
}
/** The ask by its id, open or cut. */
function askOf(d, id) {
    return openAskOf(d, id) ?? cutAskOf(d, id);
}
/**
 * Closes the ask, open or cut (an effect even when there is none, for the feed); returns the one
 * it closed. A cut ask was already out of `asks`: its row's state is what this says.
 */
function closeAsk(d, fx, id, state, extra = {}, restore = true) {
    const ask = askOf(d, id);
    d.asks = d.asks.filter((a) => a.id !== id);
    if (cutAskOf(d, id))
        d.fold.cutAsks = (d.fold.cutAsks ?? []).filter((c) => c.id !== id);
    fx.closed.push({ id, state, ...extra });
    if (restore && ask && !hasAsk(d, laneOfAsk(ask)))
        patchAgent(d, laneOfAsk(ask), (a) => (a.state === "waiting" ? { state: "working" } : {}));
    return ask;
}
/** A requires_action idle names the asks the engine waits on (its stop_reason's event_ids). */
function nameAsks(d, payload) {
    const stop = isRecord(payload) && isRecord(payload.stop_reason) ? payload.stop_reason : null;
    if (stop?.type !== "requires_action" || !Array.isArray(stop.event_ids))
        return;
    const ids = new Set(stop.event_ids.filter((id) => typeof id === "string"));
    if (d.asks.some((a) => ids.has(a.id) && !a.named))
        d.asks = d.asks.map((a) => (ids.has(a.id) && !a.named ? { ...a, named: true } : a));
}
/** Records how an ask settled, the latest last, keeping the last SETTLED_ASKS_MAX. */
function settleAsk(d, id, state) {
    const prior = d.settledAsks ?? {};
    if (Object.hasOwn(prior, id) && prior[id] === state)
        return;
    const next = {};
    const kept = Object.keys(prior).filter((k) => k !== id);
    for (const k of kept.slice(Math.max(0, kept.length - SETTLED_ASKS_MAX + 1)))
        next[k] = prior[k];
    next[id] = state;
    d.settledAsks = next;
}
function settledOf(d, id) {
    const settled = d.settledAsks ?? {};
    return Object.hasOwn(settled, id) ? settled[id] : undefined;
}
/** The calls a tool result ends: every id it names, of either kind (the server's ledger reads each). */
function resultIds(payload) {
    const p = isRecord(payload) ? payload : {};
    const ids = [str(p.tool_use_id), str(p.mcp_tool_use_id), str(p.custom_tool_use_id)];
    return [...new Set(ids.filter((id) => id !== undefined))];
}
/**
 * The session left its pause for good: open asks are moot, and so are the ones an interrupt or a
 * verdict cut. Agent states stay as the lifecycle rows set them. `keepRunThreads`: an ask on a
 * workflow run's thread stays open (the Session threads page: the client must still answer it);
 * its answer or its thread's end closes it.
 */
function cancelAsks(d, fx, opts = {}) {
    for (const a of [...d.asks]) {
        if (opts.keepRunThreads && onRunThread(d, a))
            continue;
        closeAsk(d, fx, a.id, "cancelled", {}, false);
    }
    // The turn is over: the asks an interrupt or a verdict cut read cancelled too.
    for (const a of d.fold.cutAsks ?? [])
        closeAsk(d, fx, a.id, "cancelled", {}, false);
}
function onRunThread(d, a) {
    return a.thread !== null && agentOf(d, laneOfAsk(a)).workflowRunId !== null;
}
/** Takes the open asks `which` picks out of `asks` into the fold's cut list (the newest last, bounded). */
function cutAsks(d, which) {
    const cut = d.asks.filter(which);
    if (cut.length === 0)
        return;
    d.asks = d.asks.filter((a) => !which(a));
    const kept = (d.fold.cutAsks ?? []).filter((c) => !cut.some((a) => a.id === c.id));
    const next = [...kept, ...cut.map(({ id, kind, thread }) => ({ id, kind, thread }))];
    d.fold.cutAsks = next.slice(Math.max(0, next.length - SETTLED_ASKS_MAX));
}
//# sourceMappingURL=run-view.js.map